/**
 * Local persistence for the secret-chat subsystem.
 *
 * Uses IndexedDB exclusively. Three object stores:
 *
 *   - `identity`           : { id: 'self', signing: CryptoKeyPair,
 *                              identityDh: CryptoKeyPair, createdAt: number }
 *   - `signedPrekey`       : { keyId: number, pair: CryptoKeyPair, createdAt }
 *   - `oneTimePrekeys`     : keyed by keyId, value is CryptoKeyPair
 *   - `sessions`           : keyed by chatId, value is the serialized SessionState
 *                            plus persisted metadata (peer fingerprint, verified
 *                            flag, direction, etc.)
 *   - `meta`               : misc small key/value rows (next signed-prekey id,
 *                            next OPK id, etc.)
 *
 * IndexedDB stores `CryptoKey` objects through structured-clone, so we don't
 * need to export private key material to disk. The keys remain bound to the
 * origin's IDB and inherit the standard browser sandbox guarantees. They are
 * not encrypted at rest beyond what the browser provides — the threat model
 * here is "remote attacker without the device", not "attacker with disk
 * access". This is documented in `docs/SECRET_CHATS.md` and `SECURITY.md`.
 */

const DB_NAME = 'irchat-secret-chat';
const DB_VERSION = 1;

export interface StoredIdentity {
  id: 'self';
  signing: CryptoKeyPair;
  identityDh: CryptoKeyPair;
  createdAt: number;
}

export interface StoredSignedPrekey {
  keyId: number;
  pair: CryptoKeyPair;
  createdAt: number;
}

export interface StoredOneTimePrekey {
  keyId: number;
  pair: CryptoKeyPair;
}

/** v1 session — symmetric ratchet only. */
export interface StoredSession {
  chatId: string;
  peerUserId: string;
  /** base64url SPKI of the peer's identity signing key, captured at first contact. */
  peerIkSignPubSpki: string;
  /** base64url SPKI of our identity signing key, snapshotted at session creation. */
  ourIkSignPubSpki: string;
  direction: 'I' | 'R';
  /** Has the user manually marked the safety number as verified? */
  verified: boolean;
  /** Cached safety number (so the UI doesn't recompute on every render). */
  safetyNumber: string;
  /** Send chain key (raw 32 bytes). */
  ckSend: Uint8Array;
  ns: number;
  /** Receive chain key (raw 32 bytes). */
  ckRecv: Uint8Array;
  nr: number;
  /** Skipped message keys: chain index → 32 raw bytes. */
  mkSkipped: Array<[number, Uint8Array]>;
  createdAt: number;
  updatedAt: number;
}

/**
 * v2 session — Double Ratchet (DH ratchet + symmetric ratchet).
 * Provides post-compromise security on top of v1's forward secrecy.
 */
export interface StoredSessionV2 {
  version: 2;
  chatId: string;
  peerUserId: string;
  peerIkSignPubSpki: string;
  ourIkSignPubSpki: string;
  direction: 'I' | 'R';
  verified: boolean;
  safetyNumber: string;
  /** Root key — evolves on each DH ratchet step (raw 32 bytes). */
  rootKey: Uint8Array;
  /** Our current DH ratchet keypair. CryptoKey — structured-cloned by IDB. */
  DHs: CryptoKeyPair;
  /** Their current DH ratchet public key (null until first recv). */
  DHr: CryptoKey | null;
  /** Send chain key (null until first DH ratchet completes for us). */
  CKs: Uint8Array | null;
  /** Receive chain key (null until first message from peer). */
  CKr: Uint8Array | null;
  ns: number;
  nr: number;
  /** Previous send chain length (for skip accounting). */
  pn: number;
  /** Skipped message keys: `${dhPubB64}:${n}` → raw 32 bytes. */
  mkSkipped: Array<[string, Uint8Array]>;
  createdAt: number;
  updatedAt: number;
}

/** Union type — returned by getSession. Check `'version' in s && s.version === 2` for v2. */
export type AnyStoredSession = StoredSession | StoredSessionV2;

/** Type guard for v2 sessions. */
export function isSessionV2(s: AnyStoredSession): s is StoredSessionV2 {
  return 'version' in s && (s as StoredSessionV2).version === 2;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('identity')) {
        db.createObjectStore('identity', { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains('signedPrekey')) {
        db.createObjectStore('signedPrekey', { keyPath: 'keyId' });
      }
      if (!db.objectStoreNames.contains('oneTimePrekeys')) {
        db.createObjectStore('oneTimePrekeys', { keyPath: 'keyId' });
      }
      if (!db.objectStoreNames.contains('sessions')) {
        db.createObjectStore('sessions', { keyPath: 'chatId' });
      }
      if (!db.objectStoreNames.contains('meta')) {
        db.createObjectStore('meta', { keyPath: 'key' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IDB open failed'));
  });
  return dbPromise;
}

function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error ?? new Error('IDB tx aborted'));
  });
}

function reqDone<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// ── Identity ────────────────────────────────────────────────────────────────

export async function getIdentity(): Promise<StoredIdentity | null> {
  const db = await openDb();
  const tx = db.transaction('identity', 'readonly');
  const r = await reqDone(tx.objectStore('identity').get('self'));
  return (r as StoredIdentity | undefined) ?? null;
}

export async function putIdentity(id: StoredIdentity): Promise<void> {
  const db = await openDb();
  const tx = db.transaction('identity', 'readwrite');
  tx.objectStore('identity').put(id);
  await txDone(tx);
}

// ── Signed prekey ──────────────────────────────────────────────────────────

export async function getSignedPrekey(keyId: number): Promise<StoredSignedPrekey | null> {
  const db = await openDb();
  const tx = db.transaction('signedPrekey', 'readonly');
  const r = await reqDone(tx.objectStore('signedPrekey').get(keyId));
  return (r as StoredSignedPrekey | undefined) ?? null;
}

export async function putSignedPrekey(spk: StoredSignedPrekey): Promise<void> {
  const db = await openDb();
  const tx = db.transaction('signedPrekey', 'readwrite');
  tx.objectStore('signedPrekey').put(spk);
  await txDone(tx);
}

export async function getLatestSignedPrekey(): Promise<StoredSignedPrekey | null> {
  const db = await openDb();
  const tx = db.transaction('signedPrekey', 'readonly');
  const all = (await reqDone(tx.objectStore('signedPrekey').getAll())) as StoredSignedPrekey[];
  if (all.length === 0) return null;
  all.sort((a, b) => b.keyId - a.keyId);
  return all[0];
}

// ── One-time prekeys ────────────────────────────────────────────────────────

export async function putOneTimePrekey(opk: StoredOneTimePrekey): Promise<void> {
  const db = await openDb();
  const tx = db.transaction('oneTimePrekeys', 'readwrite');
  tx.objectStore('oneTimePrekeys').put(opk);
  await txDone(tx);
}

export async function takeOneTimePrekey(keyId: number): Promise<StoredOneTimePrekey | null> {
  const db = await openDb();
  const tx = db.transaction('oneTimePrekeys', 'readwrite');
  const store = tx.objectStore('oneTimePrekeys');
  const r = (await reqDone(store.get(keyId))) as StoredOneTimePrekey | undefined;
  if (r) store.delete(keyId);
  await txDone(tx);
  return r ?? null;
}

export async function listOneTimePrekeyIds(): Promise<number[]> {
  const db = await openDb();
  const tx = db.transaction('oneTimePrekeys', 'readonly');
  const r = (await reqDone(tx.objectStore('oneTimePrekeys').getAllKeys())) as number[];
  return r;
}

// ── Sessions ───────────────────────────────────────────────────────────────

export async function getSession(chatId: string): Promise<AnyStoredSession | null> {
  const db = await openDb();
  const tx = db.transaction('sessions', 'readonly');
  const r = await reqDone(tx.objectStore('sessions').get(chatId));
  return (r as AnyStoredSession | undefined) ?? null;
}

export async function putSession(session: AnyStoredSession): Promise<void> {
  const db = await openDb();
  const tx = db.transaction('sessions', 'readwrite');
  tx.objectStore('sessions').put({ ...session, updatedAt: Date.now() });
  await txDone(tx);
}

export async function deleteSession(chatId: string): Promise<void> {
  const db = await openDb();
  const tx = db.transaction('sessions', 'readwrite');
  tx.objectStore('sessions').delete(chatId);
  await txDone(tx);
}

export async function listSessions(): Promise<StoredSession[]> {
  const db = await openDb();
  const tx = db.transaction('sessions', 'readonly');
  return (await reqDone(tx.objectStore('sessions').getAll())) as StoredSession[];
}

// ── Misc meta ──────────────────────────────────────────────────────────────

export async function getMeta<T = unknown>(key: string): Promise<T | null> {
  const db = await openDb();
  const tx = db.transaction('meta', 'readonly');
  const r = (await reqDone(tx.objectStore('meta').get(key))) as { key: string; value: T } | undefined;
  return r ? r.value : null;
}

export async function setMeta<T = unknown>(key: string, value: T): Promise<void> {
  const db = await openDb();
  const tx = db.transaction('meta', 'readwrite');
  tx.objectStore('meta').put({ key, value });
  await txDone(tx);
}

/** Wipe everything. Used by "destroy all secret chats". */
export async function wipeSecretChatStore(): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(
    ['identity', 'signedPrekey', 'oneTimePrekeys', 'sessions', 'meta'],
    'readwrite',
  );
  tx.objectStore('identity').clear();
  tx.objectStore('signedPrekey').clear();
  tx.objectStore('oneTimePrekeys').clear();
  tx.objectStore('sessions').clear();
  tx.objectStore('meta').clear();
  await txDone(tx);
}
